import React, { cloneElement, JSXElementConstructor, ReactElement, useState } from 'react'

import { Wrap } from '../Base/Base'
import { Box } from '../Box/Box'
import { Button } from '../Button/Button'
import { SimpleDialog } from './SimpleDialog'

interface ConfirmDialogProps {
  isOpen?: boolean
  dialogContent: React.ReactNode
  confirmLabel?: React.ReactNode
  cancelLabel?: React.ReactNode
  children?: ReactElement<any, string | JSXElementConstructor<any>>
  handleConfirm: () => Promise<unknown>
  handleError?: (err: any) => void
  width?: string
  overrideOnClick?: () => void // used to override the default onClick behavior of child button, which sets the dialog to Open
  finalConfirmation?: boolean
  finalConfirmationDialogContent?: React.ReactNode
}

export function ConfirmDialog({
  isOpen: isInitiallyOpen,
  children,
  handleConfirm,
  handleError,
  dialogContent,
  confirmLabel,
  cancelLabel,
  width,
  overrideOnClick,
  finalConfirmation = false,
  finalConfirmationDialogContent,
}: ConfirmDialogProps) {
  const [isOpen, setIsOpen] = useState(isInitiallyOpen || false)
  const [isLoading, setIsLoading] = useState(false)
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false)

  async function onSubmit() {
    if (finalConfirmation && !showFinalConfirmation) {
      setShowFinalConfirmation(true)
      return
    }
    setIsLoading(true)
    try {
      await handleConfirm()
      setIsOpen(false)
    } catch (err) {
      if (handleError) {
        handleError(err)
      } else {
        console.error(err)
      }
    }
    setIsLoading(false)
  }

  return (
    <>
      <SimpleDialog isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <Wrap width={width || '700px'} maxWidth="100%">
          {!showFinalConfirmation && dialogContent}
          {showFinalConfirmation && finalConfirmationDialogContent}
          <Box mt={6} />
          <Button margin="0 auto" variant="primary" size="large" onClick={onSubmit} isLoading={isLoading}>
            {confirmLabel || 'Confirm'}
          </Button>
          <Box m={3} />
          <Button
            margin="0 auto"
            variant="text"
            onClick={() => (showFinalConfirmation ? setShowFinalConfirmation(false) : setIsOpen(false))}
          >
            {cancelLabel || 'Cancel'}
          </Button>
        </Wrap>
      </SimpleDialog>
      {children
        ? cloneElement(children, {
            isLoading,
            onClick: () => {
              if (overrideOnClick) {
                overrideOnClick()
              } else {
                setIsOpen(true)
              }
            },
          })
        : null}
    </>
  )
}
